import { clsx } from "clsx";
import type { SubjectDisplayState } from "@/hooks/useMaritimeQualifications";
import { Checkbox } from "./ui/Checkbox";

type Props = {
  subjects: SubjectDisplayState[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}`;
};

const StatusBadge = ({
  status,
  expiresAt,
}: {
  status: "acquired" | "expired" | "not_acquired";
  expiresAt: string | null;
}) => {
  if (status === "acquired" && expiresAt) {
    return (
      <span className="rounded-full bg-success-bg px-2.5 py-1 text-success-text text-xs">
        〜{formatDate(expiresAt)}
      </span>
    );
  }

  if (status === "expired") {
    return (
      <span className="rounded-full bg-warning-bg px-2.5 py-1 text-warning-text text-xs">
        期限切れ
      </span>
    );
  }

  // status === "not_acquired"
  return null;
};

export const SubjectCheckboxList = ({ subjects, selectedIds, onSelectionChange }: Props) => {
  const handleToggle = (id: number, isDisabled: boolean) => {
    if (isDisabled) return;

    const newIds = new Set(selectedIds);
    if (newIds.has(id)) {
      newIds.delete(id);
    } else {
      newIds.add(id);
    }
    onSelectionChange(newIds);
  };

  return (
    <div className="mb-5">
      <span className="mb-2 block font-semibold text-ocean-800 text-sm">科目を選択</span>
      <p className="mb-3 text-[13px] text-ocean-500">合格した科目にチェックを入れてください</p>
      <div className="space-y-2">
        {subjects.map((subject) => {
          const { qualificationType, userQualification, status, isDisabled } = subject;
          const isChecked = isDisabled || selectedIds.has(qualificationType.id);

          return (
            <div
              key={qualificationType.id}
              className={clsx(
                "overflow-hidden rounded-xl border transition-all",
                status === "expired"
                  ? "border-warning-border bg-warning-bg/30"
                  : "border-ocean-100 bg-white",
                isDisabled && "opacity-60",
              )}
            >
              <Checkbox
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => handleToggle(qualificationType.id, isDisabled)}
                label={qualificationType.name}
                badge={
                  <StatusBadge status={status} expiresAt={userQualification?.expiresAt ?? null} />
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
